import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { 
  Palette, Image, Download, Loader2, Sparkles, 
  Building2, Wand2, Box, FileImage, CheckCircle,
  Zap, FileText, Plus, Trash2, Edit3, Mail, FolderOpen, Send, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// DHR Products for Company Profile
const DEFAULT_DHR_PRODUCTS = [
  { key: "fire_stop_sealant", name: "Fire Stop Sealant", nameAr: "مانع تسرب الحريق", description: "Professional fire safety sealant for penetration sealing", features: ["Fire ratings up to 4 hours", "UL Listed and FM Approved", "Easy application"] },
  { key: "expansion_joint", name: "Expansion Joint Cover", nameAr: "غطاء فاصل التمدد", description: "Engineered systems for building movement", features: ["Heavy-duty aluminum construction", "Movement capacity ±25mm to ±500mm", "Fire-rated options"] },
  { key: "raised_floor", name: "Raised Floor System", nameAr: "نظام الأرضيات المرفوعة", description: "Modular access flooring for data centers", features: ["Load capacity 500-3000 kg", "Anti-static options", "Easy cable management"] },
  { key: "rubber_protection", name: "Rubber Protection Systems", nameAr: "أنظمة الحماية المطاطية", description: "Corner guards, wall guards, wheel stoppers, speed humps", features: ["High visibility markings", "UV resistant", "Easy installation"] },
  { key: "floor_mat", name: "Aluminum Floor Mat", nameAr: "سجادة أرضية ألمنيوم", description: "Premium entrance matting systems", features: ["Heavy-duty aluminum frame", "Interchangeable inserts", "Custom sizes"] },
  { key: "pvc_handrail", name: "PVC Handrail", nameAr: "درابزين PVC", description: "Hygienic handrail for healthcare facilities", features: ["Antibacterial surface", "Multiple colors", "Impact resistant"] },
  { key: "toilet_partition", name: "Phenolic Toilet Partition", nameAr: "فواصل حمامات فينولية", description: "Waterproof restroom partitions", features: ["100% waterproof", "Graffiti resistant", "Premium hardware"] },
  { key: "stair_nosing", name: "Stair Nosing", nameAr: "حماية حافة الدرج", description: "Anti-slip stair edge protection", features: ["Aluminum with anti-slip insert", "High visibility", "Easy retrofit"] },
  { key: "steel_doors", name: "Steel Doors", nameAr: "أبواب حديدية", description: "Fire-rated and security doors", features: ["Fire ratings 30min to 4hrs", "Acoustic options", "Custom sizes"] }
];

const DesignStudioPage = () => {
  const [activeTab, setActiveTab] = useState('ideogram');
  const [generating, setGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [generatedImages, setGeneratedImages] = useState([]);
  
  // Ideogram Professional Design
  const [designType, setDesignType] = useState('product_page');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [features, setFeatures] = useState('');
  const [ideogramStyle, setIdeogramStyle] = useState('RENDER_3D');
  const [aspectRatio, setAspectRatio] = useState('ASPECT_3_4');
  
  // NEW: Logo and custom description
  const [companyLogo, setCompanyLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [imageDescription, setImageDescription] = useState('');
  const [preferredColors, setPreferredColors] = useState('');
  
  // Company Profile
  const [companyName, setCompanyName] = useState('Distinct High Road');
  const [tagline, setTagline] = useState('Excellence in Construction Solutions');
  const [products, setProducts] = useState(DEFAULT_DHR_PRODUCTS);
  const [selectedProducts, setSelectedProducts] = useState(DEFAULT_DHR_PRODUCTS.map(p => p.key));
  const [profileProgress, setProfileProgress] = useState({ current: 0, total: 0, status: '' });
  
  // Add New Product Form
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    nameAr: '',
    description: '',
    features: ''
  });
  const [editingProduct, setEditingProduct] = useState(null);

  // Add new product
  const handleAddProduct = () => {
    if (!newProduct.name || !newProduct.description) {
      toast.error('يرجى إدخال اسم المنتج والوصف');
      return;
    }
    
    const key = `custom_${Date.now()}`;
    const featuresArray = newProduct.features.split(',').map(f => f.trim()).filter(f => f);
    
    const product = {
      key,
      name: newProduct.name,
      nameAr: newProduct.nameAr || newProduct.name,
      description: newProduct.description,
      features: featuresArray.length > 0 ? featuresArray : ['Premium quality', 'Professional grade']
    };
    
    setProducts([...products, product]);
    setSelectedProducts([...selectedProducts, key]);
    setNewProduct({ name: '', nameAr: '', description: '', features: '' });
    setShowAddProduct(false);
    toast.success('تم إضافة المنتج بنجاح! ✅');
  };

  // Edit product
  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setNewProduct({
      name: product.name,
      nameAr: product.nameAr || '',
      description: product.description,
      features: product.features.join(', ')
    });
    setShowAddProduct(true);
  };

  // Save edited product
  const handleSaveEdit = () => {
    if (!newProduct.name || !newProduct.description) {
      toast.error('يرجى إدخال اسم المنتج والوصف');
      return;
    }
    
    const featuresArray = newProduct.features.split(',').map(f => f.trim()).filter(f => f);
    
    setProducts(products.map(p => 
      p.key === editingProduct.key 
        ? {
            ...p,
            name: newProduct.name,
            nameAr: newProduct.nameAr || newProduct.name,
            description: newProduct.description,
            features: featuresArray.length > 0 ? featuresArray : p.features
          }
        : p
    ));
    
    setNewProduct({ name: '', nameAr: '', description: '', features: '' });
    setEditingProduct(null);
    setShowAddProduct(false);
    toast.success('تم تحديث المنتج! ✅');
  };

  // Delete product
  const handleDeleteProduct = (key) => {
    setProducts(products.filter(p => p.key !== key));
    setSelectedProducts(selectedProducts.filter(k => k !== key));
    toast.success('تم حذف المنتج');
  };

  // Handle logo upload
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCompanyLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      toast.success('تم تحميل الشعار بنجاح');
    }
  };

  // Remove logo
  const handleRemoveLogo = () => {
    setCompanyLogo(null);
    setLogoPreview(null);
  };

  const handleGenerateIdeogram = async () => {
    if (!title) {
      toast.error('يرجى إدخال العنوان');
      return;
    }

    setGenerating(true);
    setGeneratedImage(null);

    try {
      const formData = new FormData();
      formData.append('design_type', designType);
      formData.append('title', title);
      formData.append('subtitle', subtitle);
      formData.append('features', features);
      formData.append('company_name', 'DHR - Distinct High Road');
      formData.append('style', ideogramStyle);
      formData.append('aspect_ratio', aspectRatio);
      
      // NEW: Add custom description and colors
      if (imageDescription) {
        formData.append('image_description', imageDescription);
      }
      if (preferredColors) {
        formData.append('preferred_colors', preferredColors);
      }
      
      // NEW: Add logo if uploaded
      if (companyLogo) {
        formData.append('logo', companyLogo);
      }

      const response = await api.post('/design/ideogram/professional', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setGeneratedImage(response.data);
        toast.success('تم إنشاء التصميم بنجاح! ✨');
      } else {
        toast.error(response.data.error || 'فشل إنشاء التصميم');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'فشل إنشاء التصميم');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateCompanyProfile = async () => {
    if (selectedProducts.length === 0) {
      toast.error('يرجى اختيار منتج واحد على الأقل');
      return;
    }

    setGenerating(true);
    setGeneratedImages([]);
    const totalSteps = selectedProducts.length + 1; // +1 for cover
    setProfileProgress({ current: 0, total: totalSteps, status: 'جاري إنشاء الغلاف...' });

    try {
      const images = [];
      
      // 1. Generate Cover
      const coverResponse = await api.post('/design/ideogram/profile-cover', 
        new URLSearchParams({
          company_name: companyName,
          tagline: tagline,
          industry: 'Construction Materials Supply and Installation'
        }), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      
      if (coverResponse.data.success) {
        images.push({
          type: 'cover',
          name: 'Company Profile Cover',
          ...coverResponse.data
        });
      }
      setProfileProgress({ current: 1, total: totalSteps, status: 'تم إنشاء الغلاف' });

      // 2. Generate Product Pages
      for (let i = 0; i < selectedProducts.length; i++) {
        const productKey = selectedProducts[i];
        const product = products.find(p => p.key === productKey);
        
        if (product) {
          setProfileProgress({ 
            current: i + 2, 
            total: totalSteps, 
            status: `جاري إنشاء صفحة ${product.nameAr || product.name}...` 
          });

          const formData = new URLSearchParams();
          formData.append('product_name', product.name);
          formData.append('product_description', product.description);
          formData.append('features', product.features.join(','));
          formData.append('company_name', companyName);

          const productResponse = await api.post('/design/ideogram/product-page', formData, {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
          });

          if (productResponse.data.success) {
            images.push({
              type: 'product',
              name: product.name,
              nameAr: product.nameAr,
              ...productResponse.data
            });
          }

          // Small delay between requests
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      setGeneratedImages(images);
      setProfileProgress({ current: totalSteps, total: totalSteps, status: 'تم الإنتهاء!' });
      toast.success(`تم إنشاء Company Profile بنجاح! (${images.length} صفحات) 🎉`);
      
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'فشل إنشاء البروفايل');
    } finally {
      setGenerating(false);
    }
  };

  const downloadImage = (filename) => {
    window.open(`${API_URL}/api/design/ideogram/image/${filename}/download`, '_blank');
  };

  const downloadAllImages = () => {
    generatedImages.forEach((img, index) => {
      setTimeout(() => {
        if (img.file_name) {
          downloadImage(img.file_name);
        }
      }, index * 500);
    });
  };

  // New: Generate PDF from all images
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [generatedPdf, setGeneratedPdf] = useState(null);
  
  // New: Email PDF
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailData, setEmailData] = useState({
    recipient_email: '',
    recipient_name: '',
    subject: 'Company Profile - Distinct High Road',
    message: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // New: Existing images from server
  const [existingImages, setExistingImages] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  
  // New: Upload images
  const [uploadingImages, setUploadingImages] = useState(false);

  // Upload ZIP or multiple images
  const handleUploadImages = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingImages(true);
    
    try {
      const formData = new FormData();
      
      // Check if it's a ZIP file
      if (files.length === 1 && files[0].name.toLowerCase().endsWith('.zip')) {
        formData.append('file', files[0]);
        const response = await api.post('/design/upload-images-zip', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.success) {
          toast.success(`تم استخراج ${response.data.count} صورة من الملف المضغوط 📂`);
          // Reload existing images
          await loadExistingImages();
        }
      } else {
        // Multiple images
        for (let i = 0; i < files.length; i++) {
          formData.append('files', files[i]);
        }
        
        const response = await api.post('/design/upload-multiple-images', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.success) {
          toast.success(`تم رفع ${response.data.count} صورة بنجاح 📤`);
          // Reload existing images
          await loadExistingImages();
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'فشل رفع الصور');
    } finally {
      setUploadingImages(false);
      // Reset input
      e.target.value = '';
    }
  };

  // Load existing images from server
  const loadExistingImages = async () => {
    setLoadingExisting(true);
    try {
      const response = await api.get('/design/ideogram/list-images');
      if (response.data.success) {
        setExistingImages(response.data.images);
        // Also populate generatedImages for display
        const images = response.data.images.map((img, idx) => ({
          type: idx === 0 ? 'cover' : 'product',
          name: `صفحة ${idx + 1}`,
          file_name: img.file_name
        }));
        setGeneratedImages(images);
        toast.success(`تم تحميل ${response.data.count} صورة من الخادم 📂`);
      }
    } catch (error) {
      console.error('Error loading images:', error);
      toast.error('فشل تحميل الصور');
    } finally {
      setLoadingExisting(false);
    }
  };

  // Combine existing images to PDF
  const handleCombineExistingToPdf = async (count = 10) => {
    setGeneratingPdf(true);
    try {
      const formData = new URLSearchParams();
      formData.append('count', count.toString());
      formData.append('company_name', companyName);

      const response = await api.post('/design/ideogram/combine-existing', formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      if (response.data.success) {
        setGeneratedPdf(response.data);
        toast.success(`تم إنشاء PDF بنجاح! (${response.data.total_pages} صفحات) 📄`);
      } else {
        toast.error('فشل إنشاء PDF');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'فشل إنشاء PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Send PDF via email
  const handleSendPdfEmail = async () => {
    if (!generatedPdf?.file_name) {
      toast.error('يرجى إنشاء PDF أولاً');
      return;
    }
    if (!emailData.recipient_email) {
      toast.error('يرجى إدخال البريد الإلكتروني');
      return;
    }

    setSendingEmail(true);
    try {
      const formData = new URLSearchParams();
      formData.append('pdf_filename', generatedPdf.file_name);
      formData.append('recipient_email', emailData.recipient_email);
      formData.append('recipient_name', emailData.recipient_name);
      formData.append('subject', emailData.subject);
      formData.append('message', emailData.message);

      const response = await api.post('/design/pdf/send-email', formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      if (response.data.success) {
        toast.success(`تم إرسال PDF بنجاح إلى ${emailData.recipient_email} ✉️`);
        setShowEmailDialog(false);
        setEmailData({
          recipient_email: '',
          recipient_name: '',
          subject: 'Company Profile - Distinct High Road',
          message: ''
        });
      } else {
        toast.error('فشل إرسال البريد');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'فشل إرسال البريد');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (generatedImages.length === 0) {
      toast.error('لا توجد صور لتجميعها');
      return;
    }

    setGeneratingPdf(true);
    try {
      // Get filenames
      const filenames = generatedImages
        .filter(img => img.file_name)
        .map(img => img.file_name)
        .join(',');

      // Get page names for TOC
      const pageNames = generatedImages
        .filter(img => img.file_name)
        .map(img => img.type === 'cover' ? 'Cover Page' : img.name || 'Product Page')
        .join(',');

      const formData = new URLSearchParams();
      formData.append('filenames', filenames);
      formData.append('page_names', pageNames);
      formData.append('add_logo', 'true');
      formData.append('add_toc', 'true');
      formData.append('company_name', companyName);
      formData.append('page_size', 'A4');

      const response = await api.post('/design/ideogram/combine-pdf', formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      if (response.data.success) {
        setGeneratedPdf(response.data);
        toast.success(`تم إنشاء PDF بنجاح! (${response.data.total_pages} صفحات مع جدول المحتويات) 📄`);
      } else {
        toast.error('فشل إنشاء PDF');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'فشل إنشاء PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const downloadPdf = () => {
    if (generatedPdf?.file_name) {
      window.open(`${API_URL}/api/design/pdf/${generatedPdf.file_name}/download`, '_blank');
    }
  };

  const designTypes = [
    { value: 'product_page', label: 'صفحة منتج', icon: '📦' },
    { value: 'company_profile', label: 'صفحة بروفايل', icon: '🏢' },
    { value: 'poster', label: 'بوستر إعلاني', icon: '📄' },
    { value: 'flyer', label: 'فلاير', icon: '📋' },
    { value: 'social_media', label: 'سوشيال ميديا', icon: '📱' },
    { value: 'product_showcase', label: 'عرض منتج 3D', icon: '🎨' }
  ];

  const styles = [
    { value: 'RENDER_3D', label: '3D واقعي' },
    { value: 'REALISTIC', label: 'واقعي' },
    { value: 'DESIGN', label: 'تصميم جرافيك' }
  ];

  const aspectRatios = [
    { value: 'ASPECT_1_1', label: 'مربع (1:1)' },
    { value: 'ASPECT_3_4', label: 'عمودي (3:4)' },
    { value: 'ASPECT_4_3', label: 'أفقي (4:3)' },
    { value: 'ASPECT_16_9', label: 'عريض (16:9)' },
    { value: 'ASPECT_9_16', label: 'ستوري (9:16)' }
  ];

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Palette className="h-7 w-7 text-blue-600" />
            استوديو التصميم الاحترافي
          </h1>
          <p className="text-gray-500 mt-1">
            <span className="inline-flex items-center gap-1 text-green-600 font-medium">
              <Zap className="h-4 w-4" />
              مدعوم بـ Ideogram AI - نصوص واضحة 100%
            </span>
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="ideogram" className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            تصميم احترافي
          </TabsTrigger>
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Company Profile كامل
          </TabsTrigger>
        </TabsList>

        {/* Professional Design Tab */}
        <TabsContent value="ideogram">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Box className="h-5 w-5 text-purple-600" />
                  تصميم احترافي مع Ideogram AI
                </CardTitle>
                <CardDescription>
                  صور 3D احترافية مع نصوص واضحة ومقروءة
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Logo Upload */}
                <div>
                  <Label>شعار الشركة</Label>
                  <div className="mt-1 flex items-center gap-3">
                    {logoPreview ? (
                      <div className="relative">
                        <img 
                          src={logoPreview} 
                          alt="Logo" 
                          className="h-16 w-auto rounded border p-1 bg-white"
                        />
                        <button
                          onClick={handleRemoveLogo}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="h-16 w-24 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 text-xs">
                        لا يوجد شعار
                      </div>
                    )}
                    <div className="relative">
                      <input
                        type="file"
                        id="logo-upload"
                        className="hidden"
                        accept="image/*"
                        onChange={handleLogoUpload}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('logo-upload').click()}
                      >
                        <Upload className="h-4 w-4 ml-1" />
                        {logoPreview ? 'تغيير' : 'تحميل شعار'}
                      </Button>
                    </div>
                  </div>
                </div>

                <div>
                  <Label>نوع التصميم</Label>
                  <Select value={designType} onValueChange={setDesignType}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {designTypes.map(t => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.icon} {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>العنوان الرئيسي *</Label>
                  <Input
                    placeholder="مثال: Fire Stop Sealant"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>العنوان الفرعي</Label>
                  <Input
                    placeholder="مثال: Professional Fire Safety Solution"
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>المميزات (مفصولة بفاصلة)</Label>
                  <Textarea
                    placeholder="مثال: Fire ratings up to 4 hours, UL Listed, Easy application"
                    value={features}
                    onChange={(e) => setFeatures(e.target.value)}
                    rows={2}
                    className="mt-1"
                  />
                </div>

                {/* Image Description */}
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <Label className="text-purple-700 flex items-center gap-2">
                    <Wand2 className="h-4 w-4" />
                    وصف الصورة المطلوبة (اختياري)
                  </Label>
                  <Textarea
                    placeholder="صف الصورة التي تريد إنشاءها بالتفصيل... مثال: صورة 3D لعلبة منتج على خلفية بيضاء مع إضاءة احترافية"
                    value={imageDescription}
                    onChange={(e) => setImageDescription(e.target.value)}
                    rows={3}
                    className="mt-2 bg-white"
                  />
                </div>

                {/* Preferred Colors */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Palette className="h-4 w-4 text-blue-600" />
                    الألوان المفضلة (اختياري)
                  </Label>
                  <Input
                    placeholder="مثال: أزرق داكن، أبيض، ذهبي أو #1A4D91, #FFD700"
                    value={preferredColors}
                    onChange={(e) => setPreferredColors(e.target.value)}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">يمكنك كتابة أسماء الألوان أو أكواد HEX</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>النمط</Label>
                    <Select value={ideogramStyle} onValueChange={setIdeogramStyle}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {styles.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>الأبعاد</Label>
                    <Select value={aspectRatio} onValueChange={setAspectRatio}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {aspectRatios.map(a => (
                          <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button 
                  onClick={handleGenerateIdeogram}
                  disabled={generating || !title}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                  data-testid="generate-design-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-4 w-4 ml-2 animate-spin" data-testid="loading-spinner" />
                      جاري الإنشاء...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 ml-2" />
                      إنشاء التصميم
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Result Preview */}
            <Card>
              <CardHeader>
                <CardTitle>النتيجة</CardTitle>
              </CardHeader>
              <CardContent>
                {generating ? (
                  <div className="flex flex-col items-center justify-center h-80 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg">
                    <Loader2 className="h-16 w-16 text-purple-600 animate-spin mb-4" />
                    <p className="text-gray-700 font-medium">جاري إنشاء التصميم الاحترافي...</p>
                    <p className="text-sm text-gray-500 mt-2">Ideogram AI يعمل على تصميمك</p>
                  </div>
                ) : generatedImage?.file_name ? (
                  <div className="space-y-4">
                    <img
                      src={`${API_URL}/api/design/ideogram/image/${generatedImage.file_name}`}
                      alt="Generated Design"
                      className="w-full rounded-lg shadow-lg"
                    />
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => downloadImage(generatedImage.file_name)}
                        className="flex-1"
                      >
                        <Download className="h-4 w-4 ml-2" />
                        تحميل
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => window.open(`${API_URL}/api/design/ideogram/image/${generatedImage.file_name}`, '_blank')}
                      >
                        عرض كامل
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-80 bg-gray-50 rounded-lg">
                    <FileImage className="h-20 w-20 text-gray-300 mb-4" />
                    <p className="text-gray-400">التصميم سيظهر هنا</p>
                    <p className="text-sm text-gray-300 mt-2">نصوص واضحة 100% مع Ideogram AI</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Company Profile Tab */}
        <TabsContent value="profile">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Settings */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  إعدادات البروفايل
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>اسم الشركة</Label>
                  <Input
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>الشعار (Tagline)</Label>
                  <Input
                    value={tagline}
                    onChange={(e) => setTagline(e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>المنتجات ({selectedProducts.length} مختار)</Label>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setShowAddProduct(true);
                        setEditingProduct(null);
                        setNewProduct({ name: '', nameAr: '', description: '', features: '' });
                      }}
                      className="h-7 text-xs"
                      data-testid="add-product-btn"
                    >
                      <Plus className="h-3 w-3 ml-1" />
                      إضافة منتج
                    </Button>
                  </div>
                  
                  {/* Add/Edit Product Form */}
                  {showAddProduct && (
                    <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                      <p className="text-sm font-medium text-blue-700 mb-2">
                        {editingProduct ? '✏️ تعديل المنتج' : '➕ إضافة منتج جديد'}
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="اسم المنتج (بالإنجليزية) *"
                          value={newProduct.name}
                          onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                          className="text-sm h-8"
                        />
                        <Input
                          placeholder="اسم المنتج (بالعربية)"
                          value={newProduct.nameAr}
                          onChange={(e) => setNewProduct({...newProduct, nameAr: e.target.value})}
                          className="text-sm h-8"
                          dir="rtl"
                        />
                      </div>
                      <Input
                        placeholder="وصف المنتج (بالإنجليزية) *"
                        value={newProduct.description}
                        onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                        className="text-sm h-8"
                      />
                      <Input
                        placeholder="المميزات (مفصولة بفاصلة)"
                        value={newProduct.features}
                        onChange={(e) => setNewProduct({...newProduct, features: e.target.value})}
                        className="text-sm h-8"
                      />
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={editingProduct ? handleSaveEdit : handleAddProduct}
                          className="h-7 text-xs bg-blue-600 hover:bg-blue-700"
                        >
                          {editingProduct ? 'حفظ التعديل' : 'إضافة'}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setShowAddProduct(false);
                            setEditingProduct(null);
                            setNewProduct({ name: '', nameAr: '', description: '', features: '' });
                          }}
                          className="h-7 text-xs"
                        >
                          إلغاء
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
                    {products.map(product => (
                      <div key={product.key} className="flex items-center gap-2 text-sm p-2 bg-gray-50 rounded hover:bg-gray-100 group">
                        <input
                          type="checkbox"
                          checked={selectedProducts.includes(product.key)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedProducts([...selectedProducts, product.key]);
                            } else {
                              setSelectedProducts(selectedProducts.filter(k => k !== product.key));
                            }
                          }}
                          className="rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <span className="font-medium block truncate">{product.name}</span>
                          {product.nameAr && product.nameAr !== product.name && (
                            <span className="text-xs text-gray-500 block truncate">{product.nameAr}</span>
                          )}
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                          <button 
                            onClick={() => handleEditProduct(product)}
                            className="p-1 hover:bg-blue-100 rounded text-blue-600"
                            title="تعديل"
                          >
                            <Edit3 className="h-3 w-3" />
                          </button>
                          {product.key.startsWith('custom_') && (
                            <button 
                              onClick={() => handleDeleteProduct(product.key)}
                              className="p-1 hover:bg-red-100 rounded text-red-600"
                              title="حذف"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Button 
                  onClick={handleGenerateCompanyProfile}
                  disabled={generating || selectedProducts.length === 0}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600"
                  data-testid="generate-profile-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-4 w-4 ml-2 animate-spin" />
                      جاري الإنشاء...
                    </>
                  ) : (
                    <>
                      <Building2 className="h-4 w-4 ml-2" />
                      إنشاء البروفايل ({selectedProducts.length + 1} صفحات)
                    </>
                  )}
                </Button>

                {/* Progress */}
                {generating && profileProgress.total > 0 && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-blue-700">{profileProgress.status}</span>
                      <span className="text-blue-600 font-medium">
                        {profileProgress.current}/{profileProgress.total}
                      </span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${(profileProgress.current / profileProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Generated Images */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle>صفحات البروفايل</CardTitle>
                  <div className="flex gap-2 flex-wrap">
                    {/* Upload images button */}
                    <div className="relative">
                      <input
                        type="file"
                        id="upload-images"
                        className="hidden"
                        multiple
                        accept=".png,.jpg,.jpeg,.webp,.zip"
                        onChange={handleUploadImages}
                        disabled={uploadingImages}
                      />
                      <Button 
                        onClick={() => document.getElementById('upload-images').click()}
                        disabled={uploadingImages}
                        variant="outline"
                        size="sm"
                        className="border-green-500 text-green-600 hover:bg-green-50"
                        data-testid="upload-images-btn"
                      >
                        {uploadingImages ? (
                          <Loader2 className="h-4 w-4 ml-2 animate-spin" />
                        ) : (
                          <Upload className="h-4 w-4 ml-2" />
                        )}
                        رفع صور / ZIP
                      </Button>
                    </div>
                    
                    {/* Load existing images button */}
                    <Button 
                      onClick={loadExistingImages}
                      disabled={loadingExisting}
                      variant="outline"
                      size="sm"
                      data-testid="load-existing-btn"
                    >
                      {loadingExisting ? (
                        <Loader2 className="h-4 w-4 ml-2 animate-spin" />
                      ) : (
                        <FolderOpen className="h-4 w-4 ml-2" />
                      )}
                      تحميل الصور الموجودة
                    </Button>
                    
                    {/* Quick combine button */}
                    <Button 
                      onClick={() => handleCombineExistingToPdf(15)}
                      disabled={generatingPdf}
                      className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
                      size="sm"
                      data-testid="quick-combine-btn"
                    >
                      {generatingPdf ? (
                        <Loader2 className="h-4 w-4 ml-2 animate-spin" />
                      ) : (
                        <Zap className="h-4 w-4 ml-2" />
                      )}
                      تجميع سريع (15 صورة)
                    </Button>
                    
                    {generatedImages.length > 0 && (
                      <>
                        <Button 
                          onClick={handleGeneratePdf} 
                          disabled={generatingPdf}
                          className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                          size="sm"
                          data-testid="generate-pdf-btn"
                        >
                          {generatingPdf ? (
                            <Loader2 className="h-4 w-4 ml-2 animate-spin" />
                          ) : (
                            <FileText className="h-4 w-4 ml-2" />
                          )}
                          {generatingPdf ? 'جاري الإنشاء...' : 'تحميل PDF'}
                        </Button>
                        <Button onClick={downloadAllImages} variant="outline" size="sm">
                          <Download className="h-4 w-4 ml-2" />
                          تحميل الكل
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* PDF Download & Email Success */}
                {generatedPdf && (
                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="h-5 w-5" />
                        <span className="font-medium">PDF جاهز! ({generatedPdf.total_pages} صفحات)</span>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          onClick={downloadPdf} 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          data-testid="download-pdf-btn"
                        >
                          <Download className="h-4 w-4 ml-2" />
                          تحميل
                        </Button>
                        <Button 
                          onClick={() => setShowEmailDialog(true)} 
                          size="sm" 
                          variant="outline"
                          className="border-blue-500 text-blue-600 hover:bg-blue-50"
                          data-testid="email-pdf-btn"
                        >
                          <Mail className="h-4 w-4 ml-2" />
                          إرسال بالبريد
                        </Button>
                      </div>
                    </div>
                    
                    {/* Email Dialog */}
                    {showEmailDialog && (
                      <div className="mt-4 p-4 bg-white border rounded-lg space-y-3">
                        <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                          <Send className="h-4 w-4" />
                          إرسال PDF بالبريد الإلكتروني
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">البريد الإلكتروني *</Label>
                            <Input
                              type="email"
                              placeholder="example@email.com"
                              value={emailData.recipient_email}
                              onChange={(e) => setEmailData({...emailData, recipient_email: e.target.value})}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">اسم المستلم</Label>
                            <Input
                              placeholder="السيد/السيدة..."
                              value={emailData.recipient_name}
                              onChange={(e) => setEmailData({...emailData, recipient_name: e.target.value})}
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">الموضوع</Label>
                          <Input
                            value={emailData.subject}
                            onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">الرسالة (اختياري)</Label>
                          <Textarea
                            placeholder="أكتب رسالة مخصصة..."
                            value={emailData.message}
                            onChange={(e) => setEmailData({...emailData, message: e.target.value})}
                            rows={3}
                            className="mt-1"
                          />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setShowEmailDialog(false)}
                          >
                            إلغاء
                          </Button>
                          <Button 
                            onClick={handleSendPdfEmail}
                            disabled={sendingEmail || !emailData.recipient_email}
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            {sendingEmail ? (
                              <Loader2 className="h-4 w-4 ml-2 animate-spin" />
                            ) : (
                              <Send className="h-4 w-4 ml-2" />
                            )}
                            {sendingEmail ? 'جاري الإرسال...' : 'إرسال'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {generating && generatedImages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-96 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg">
                    <Loader2 className="h-16 w-16 text-blue-600 animate-spin mb-4" />
                    <p className="text-gray-700 font-medium">{profileProgress.status}</p>
                    <p className="text-sm text-gray-500 mt-2">
                      صفحة {profileProgress.current} من {profileProgress.total}
                    </p>
                  </div>
                ) : generatedImages.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-[600px] overflow-y-auto">
                    {generatedImages.map((img, index) => (
                      <div key={index} className="relative group">
                        <div className="absolute top-2 right-2 z-10">
                          <Badge variant={img.type === 'cover' ? 'default' : 'secondary'}>
                            {img.type === 'cover' ? 'غلاف' : img.name}
                          </Badge>
                        </div>
                        {img.file_name ? (
                          <>
                            <img
                              src={`${API_URL}/api/design/ideogram/image/${img.file_name}`}
                              alt={img.name}
                              className="w-full rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer"
                              onClick={() => window.open(`${API_URL}/api/design/ideogram/image/${img.file_name}`, '_blank')}
                            />
                            <Button 
                              size="sm"
                              className="absolute bottom-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => downloadImage(img.file_name)}
                            >
                              <Download className="h-3 w-3" />
                            </Button>
                          </>
                        ) : (
                          <div className="h-48 bg-red-50 rounded-lg flex items-center justify-center">
                            <p className="text-red-500 text-sm">فشل إنشاء هذه الصفحة</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-96 bg-gray-50 rounded-lg">
                    <Building2 className="h-20 w-20 text-gray-300 mb-4" />
                    <p className="text-gray-400">صفحات البروفايل ستظهر هنا</p>
                    <p className="text-sm text-gray-300 mt-2">
                      غلاف + {selectedProducts.length} صفحات منتجات
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DesignStudioPage;
